import { LinearProgress, Typography } from "@material-ui/core";
import clsx from "clsx";
import { ErrorBox } from "components/error";
import TableSet from "components/tableSetExplorer/TableSet";
import { isEmpty } from "lodash";
import { Fragment } from "react";
import CalculationNotExist from "./CalculationNotExist";
import { useStyles } from "./calculationResultStyle";
import { extractCalculationData } from "./calculationResultUtils";
import {
  useCalculationInputData,
  useCalculationOutputData,
  useQueryCalculationSchema
} from "./hooks/calculationResultHooks";
import UnCompletedCalculationState from "./UnCompletedCalculationState";

const CalculationResult = ({
  useCaseKey,
  useCaseVersion,
  calculationId
}: {
  useCaseKey: string;
  useCaseVersion: string;
  calculationId: string;
}) => {
  const {
    root,
    tableSetWrapper,
    margin,
    centered,
    leftBackgroundColor,
    rightBackgroundColor,
    marginLeft
  } = useStyles({});

  const { data, loading, error } = useQueryCalculationSchema({
    useCaseKey,
    useCaseVersion,
    calculationId
  });

  const calculation = data?.cloudPlatform.useCases?.[0]?.calculation;

  const { loadOutputData } = useCalculationOutputData();
  const { loadInputData } = useCalculationInputData();

  if (error)
    return (
      <ErrorBox apolloError={error} title="Unable to access this calculation" />
    );
  if (loading)
    return (
      <LinearProgress
        className={margin}
        data-testid={loading ? "progress-loading" : "progress-done"}
      />
    );
  if (!calculation) {
    return <CalculationNotExist />;
  }
  const {
    state,
    inputDataAddress,
    inputSchema,
    resultDataAddress,
    outputSchema,
    inputColumnDefs,
    outputColumnDefs,
    calculationError
  } = extractCalculationData(calculation);
  return (
    <div className={root} key={calculationId}>
      <div className={tableSetWrapper}>
        {isEmpty(inputSchema) ? (
          <div className={centered}>
            <Typography>No input data required.</Typography>
          </div>
        ) : (
          <TableSet
            //@ts-ignore
            tablesSchema={inputSchema}
            calculationId={calculationId}
            useCaseVersion={useCaseVersion}
            useCaseKey={useCaseKey}
            loadData={loadInputData}
            columnDefs={inputColumnDefs}
            state={state}
            dataType="input"
            chartSources="use-case-input"
            dataAddress={inputDataAddress}
            toResultChart={() => "./input-charts"}
            toCalculationResultTable={(name: string) => `./input-data/${name}`}
            backgroundColor={leftBackgroundColor}
          />
        )}
      </div>

      <div className={clsx(tableSetWrapper, marginLeft)}>
        {state && state !== "Completed" ? (
          <UnCompletedCalculationState
            state={state}
            calculationError={calculationError}
          />
        ) : (
          <Fragment>
            {isEmpty(outputSchema) ? (
              <div className={centered}>
                <Typography>This use case does not return any data.</Typography>
              </div>
            ) : (
              <TableSet
                //@ts-ignore
                tablesSchema={outputSchema}
                calculationId={calculationId}
                useCaseKey={useCaseKey}
                useCaseVersion={useCaseVersion}
                loadData={loadOutputData}
                columnDefs={outputColumnDefs}
                dataType="output"
                chartSources="use-case-result"
                dataAddress={resultDataAddress}
                toResultChart={() => "./output-charts"}
                toCalculationResultTable={(name: string) =>
                  `./output-data/${name}`
                }
                backgroundColor={rightBackgroundColor}
              />
            )}
          </Fragment>
        )}
      </div>
    </div>
  );
};
export default CalculationResult;
